/**
 * <dorahi-carousel> — vanilla port of dorahi-main/components/homepage/Carousel.jsx.
 *
 * Reproduces the React behaviour 1:1:
 *   - mouse drag scrolling with velocity tracking (snap disabled while dragging)
 *   - fling-aware snap to the nearest slide on release (velocity * 200 projection)
 *   - click suppression after a real drag (capture phase, same as onClickCapture)
 *   - progress bar width driven by the scroll position
 *   - optional prev/next buttons ([data-carousel-prev/next], showNavButtons in
 *     the React version); scroll amount = 340 + gap (data-gap, default 16)
 *
 * Touch devices use native scrolling + CSS scroll-snap, exactly like the app.
 * The .carousel-container / .carousel-scroller / .carousel-item / .is-dragging
 * styles live in assets/tailwind.input.css (ported from app/globals.css).
 */
(function () {
  if (customElements.get('dorahi-carousel')) return;

  class DorahiCarousel extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-carousel-scroller]');
      this.progressBar = this.querySelector('[data-carousel-progress]');
      if (!this.scroller) return;

      this.drag = {
        startX: 0,
        scrollLeft: 0,
        isDown: false,
        moved: false,
        velocity: 0,
        lastX: 0,
        lastTime: 0,
      };

      this.onMouseDown = this.onMouseDown.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.onMouseUp = this.onMouseUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.scroller.addEventListener('mousedown', this.onMouseDown);
      this.scroller.addEventListener('click', this.onClickCapture, true);
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

      // Nav buttons (Carousel.jsx scroll()): card width 340 + gap
      this.gap = parseFloat(this.dataset.gap || '16');
      const prevButton = this.querySelector('[data-carousel-prev]');
      const nextButton = this.querySelector('[data-carousel-next]');
      if (prevButton) prevButton.addEventListener('click', () => this.scrollByAmount(-1));
      if (nextButton) nextButton.addEventListener('click', () => this.scrollByAmount(1));
    }

    scrollByAmount(direction) {
      const scrollAmount = 340 + this.gap; // card width + gap
      this.scroller.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
    }

    disconnectedCallback() {
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
    }

    onMouseDown(event) {
      const scroller = this.scroller;
      const drag = this.drag;

      drag.isDown = true;
      drag.startX = event.pageX - scroller.offsetLeft;
      drag.scrollLeft = scroller.scrollLeft;
      drag.moved = false;
      drag.lastX = event.pageX;
      drag.lastTime = Date.now();
      drag.velocity = 0;

      scroller.classList.add('is-dragging');
      // Disable scroll snap while dragging for smooth movement
      scroller.style.scrollSnapType = 'none';

      // Global listeners so the drag continues outside the container
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp);
    }

    onMouseMove(event) {
      const drag = this.drag;
      if (!drag.isDown) return;

      event.preventDefault();
      const scroller = this.scroller;
      const x = event.pageX - scroller.offsetLeft;
      const walk = (x - drag.startX) * 1; // scroll-fast

      // Calculate velocity
      const now = Date.now();
      const dt = now - drag.lastTime;
      const dx = event.pageX - drag.lastX;
      if (dt > 0) {
        drag.velocity = dx / dt;
      }
      drag.lastX = event.pageX;
      drag.lastTime = now;

      // Check if moved significantly
      if (Math.abs(walk) > 5) {
        drag.moved = true;
      }

      scroller.scrollLeft = drag.scrollLeft - walk;
    }

    onMouseUp() {
      const drag = this.drag;
      if (!drag.isDown) return;

      drag.isDown = false;
      this.scroller.classList.remove('is-dragging');
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);

      if (drag.moved) {
        this.snapToSlide();
      } else {
        // If didn't move, re-enable snap immediately
        this.scroller.style.scrollSnapType = 'x mandatory';
      }
    }

    onClickCapture(event) {
      if (this.drag.moved) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    snapToSlide() {
      const scroller = this.scroller;
      const scrollLeft = scroller.scrollLeft;
      const clientWidth = scroller.clientWidth;
      const scrollWidth = scroller.scrollWidth;

      // If at ends, let native snap handle it
      if (scrollLeft <= 0 || scrollLeft >= scrollWidth - clientWidth) {
        scroller.style.scrollSnapType = 'x mandatory';
        return;
      }

      // Adjust target based on velocity (fling)
      const projectedScroll = scrollLeft - this.drag.velocity * 200; // Simple inertia

      let closestSlide = null;
      let minDistance = Infinity;
      Array.from(scroller.children).forEach((slide) => {
        const distance = Math.abs(slide.offsetLeft - projectedScroll);
        if (distance < minDistance) {
          minDistance = distance;
          closestSlide = slide;
        }
      });

      if (closestSlide) {
        scroller.scrollTo({ left: closestSlide.offsetLeft, behavior: 'smooth' });
      }

      // Re-enable snap once the smooth scroll has likely finished
      setTimeout(() => {
        scroller.style.scrollSnapType = 'x mandatory';
      }, 500);
    }

    onScroll() {
      if (!this.progressBar) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.scroller;
      const progress = (scrollLeft / (scrollWidth - clientWidth)) * 100;
      this.progressBar.style.width = `${Math.min(100, Math.max(0, progress))}%`;
    }
  }

  customElements.define('dorahi-carousel', DorahiCarousel);
})();
